"use client";
import React, { useState } from "react";

type DepartmentStackContextProps = {
  pushParentDepartment: (departmentId: string) => void;
  popParentDepartment: () => void;
  resetParentDepartment: () => void;
  parentDepartmentId: string | null;
  selectedDepartmentName: string | null;
  setSelectedDepartmentName: (name: string | null) => void;
};
const DepartmentStackContext =
  React.createContext<DepartmentStackContextProps | null>(null);

export const useDepartmentStackContext = () => {
  const context = React.useContext(DepartmentStackContext);
  if (!context) {
    throw new Error(
      "useDepartmentStackContext must be used within a DepartmentStackContext.Provider",
    );
  }
  return context;
};

export const DepartmentStackProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [selectedDepartmentName, setSelectedDepartmentName] = useState<
    string | null
  >(null);

  const [departmentCodesStack, setDepartmentCodesStack] = useState<string[]>(
    [],
  );

  const pushParentDepartment = (departmentCode: string) => {
    setDepartmentCodesStack((prev) => [...prev, departmentCode]);
  };

  const popParentDepartment = () => {
    if (departmentCodesStack.length > 1) {
      setDepartmentCodesStack((prev) => prev.slice(0, -1));
      return;
    }

    resetParentDepartment();
  };

  const resetParentDepartment = () => {
    setDepartmentCodesStack((prev) => prev.slice(0, 0));
    setSelectedDepartmentName(null);
  };

  const parentDepartmentId =
    departmentCodesStack[departmentCodesStack.length - 1] ?? null;

  const contextValue: DepartmentStackContextProps = {
    pushParentDepartment,
    popParentDepartment,
    resetParentDepartment,
    parentDepartmentId,
    selectedDepartmentName,
    setSelectedDepartmentName,
  };

  return (
    <DepartmentStackContext.Provider value={contextValue}>
      {children}
    </DepartmentStackContext.Provider>
  );
};
